/**
 * Canvas equivalents of the editor preview's CSS layers (transforms, filters, gradient
 * overlays) plus the server's cover-fit framing, so a frame drawn here matches what the
 * preview shows and what the server export crops.
 */

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Same clamping as server `_normalize_export_framing`. */
export function normalizeFraming(raw) {
  const f = raw && typeof raw === 'object' ? raw : {};
  const zoom = Math.max(1, Math.min(4, num(f.zoom ?? f.scale, 1) || 1));
  const panX = Math.max(0, Math.min(1, num(f.pan_x ?? f.panX, 0.5)));
  const panY = Math.max(0, Math.min(1, num(f.pan_y ?? f.panY, 0.5)));
  return { zoom, panX, panY };
}

/**
 * Destination rect for drawing a `sw×sh` source into a `tw×th` frame: scale to cover,
 * apply zoom, then pan across the overflow (server `_fit_export_frame`).
 */
export function coverFitRect(sw, sh, tw, th, framing) {
  if (!(sw > 0) || !(sh > 0)) return null;
  const { zoom, panX, panY } = normalizeFraming(framing);
  const scale = Math.max(tw / sw, th / sh) * zoom;
  const dw = Math.max(tw, Math.round(sw * scale));
  const dh = Math.max(th, Math.round(sh * scale));
  return {
    dx: -Math.round(panX * Math.max(0, dw - tw)) || 0,
    dy: -Math.round(panY * Math.max(0, dh - th)) || 0,
    dw,
    dh,
  };
}

const TRANSFORM_FN_RE = /([a-zA-Z0-9]+)\(([^)]*)\)/g;

function parseLength(raw, basis) {
  const s = String(raw || '').trim();
  if (s.endsWith('%')) return (num(s.slice(0, -1)) / 100) * basis;
  if (s.endsWith('px')) return num(s.slice(0, -2));
  return num(s);
}

function parseAngleDeg(raw) {
  const s = String(raw || '').trim();
  if (s.endsWith('rad')) return (num(s.slice(0, -3)) * 180) / Math.PI;
  if (s.endsWith('turn')) return num(s.slice(0, -4)) * 360;
  if (s.endsWith('deg')) return num(s.slice(0, -3));
  return num(s);
}

/**
 * Parse the subset of CSS transforms the preview emits (translate/translate3d/translateX/Y,
 * scale, rotate) into canvas ops. Percentages resolve against the element box.
 */
export function parseCssTransform(transform, w, h) {
  const ops = [];
  if (!transform || typeof transform !== 'string') return ops;
  for (const m of transform.matchAll(TRANSFORM_FN_RE)) {
    const fn = m[1].toLowerCase();
    const args = m[2].split(',').map((a) => a.trim()).filter(Boolean);
    if (fn === 'translate' || fn === 'translate3d') {
      ops.push({ op: 'translate', x: parseLength(args[0], w), y: parseLength(args[1] ?? '0', h) });
    } else if (fn === 'translatex') {
      ops.push({ op: 'translate', x: parseLength(args[0], w), y: 0 });
    } else if (fn === 'translatey') {
      ops.push({ op: 'translate', x: 0, y: parseLength(args[0], h) });
    } else if (fn === 'scale' || fn === 'scale3d') {
      const sx = num(args[0], 1);
      ops.push({ op: 'scale', x: sx, y: args[1] != null ? num(args[1], sx) : sx });
    } else if (fn === 'scalex') {
      ops.push({ op: 'scale', x: num(args[0], 1), y: 1 });
    } else if (fn === 'scaley') {
      ops.push({ op: 'scale', x: 1, y: num(args[0], 1) });
    } else if (fn === 'rotate' || fn === 'rotatez') {
      ops.push({ op: 'rotate', rad: (parseAngleDeg(args[0]) * Math.PI) / 180 });
    }
  }
  return ops;
}

/** Apply CSS transform ops about the box center (`transform-origin: center`). */
export function applyTransformOps(ctx, ops, w, h) {
  if (!ops.length) return;
  ctx.translate(w / 2, h / 2);
  for (const o of ops) {
    if (o.op === 'translate') ctx.translate(o.x, o.y);
    else if (o.op === 'scale') ctx.scale(o.x, o.y);
    else if (o.op === 'rotate') ctx.rotate(o.rad);
  }
  ctx.translate(-w / 2, -h / 2);
}

/** Motion `{ scale, tx, ty, rotate }` (fractions of frame / degrees) as CSS-equivalent ops. */
export function motionOps({ scale = 1, tx = 0, ty = 0, rotate = 0 } = {}, w, h) {
  const ops = [];
  if (tx || ty) ops.push({ op: 'translate', x: tx * w, y: ty * h });
  if (scale !== 1) ops.push({ op: 'scale', x: scale, y: scale });
  if (rotate) ops.push({ op: 'rotate', rad: (rotate * Math.PI) / 180 });
  return ops;
}

/**
 * Draw an already-rendered `w×h` layer with preview CSS (`transform`, `opacity`, `filter`).
 */
export function drawStyledLayer(ctx, source, w, h, style = {}) {
  if (!source) return;
  const opacity = style.opacity == null ? 1 : Math.max(0, Math.min(1, num(style.opacity, 1)));
  if (opacity <= 0.001) return;
  ctx.save();
  ctx.globalAlpha = opacity;
  if (style.filter) ctx.filter = style.filter;
  applyTransformOps(ctx, parseCssTransform(style.transform, w, h), w, h);
  ctx.drawImage(source, 0, 0, w, h);
  ctx.restore();
}

function splitTopLevel(str) {
  const out = [];
  let depth = 0;
  let cur = '';
  for (const ch of str) {
    if (ch === '(') depth += 1;
    if (ch === ')') depth -= 1;
    if (ch === ',' && depth === 0) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

function parseColorStop(raw, idx, total) {
  const m = raw.match(/^(rgba?\([^)]*\)|#[0-9a-fA-F]{3,8}|[a-zA-Z]+)\s*([\d.]+%)?$/);
  if (!m) return null;
  const pos = m[2] != null ? num(m[2].slice(0, -1)) / 100 : (total > 1 ? idx / (total - 1) : 0);
  return { color: m[1], pos: Math.max(0, Math.min(1, pos)) };
}

function addStops(gradient, stops) {
  let last = 0;
  for (const s of stops) {
    last = Math.max(last, s.pos);
    gradient.addColorStop(last, s.color);
  }
}

/**
 * Paint a preview overlay `background` (solid color, `linear-gradient(<deg>, …)`, or
 * `radial-gradient(ellipse W% H% at X% Y%, …)`) over the full frame.
 */
export function paintCssBackground(ctx, background, w, h) {
  const bg = String(background || '').replace(/\s+/g, ' ').trim();
  if (!bg) return;
  const lin = bg.match(/^linear-gradient\((.*)\)$/i);
  const rad = bg.match(/^radial-gradient\((.*)\)$/i);
  ctx.save();
  if (lin) {
    const parts = splitTopLevel(lin[1]);
    let angle = 180;
    if (/deg|rad|turn/.test(parts[0])) angle = parseAngleDeg(parts.shift());
    const stops = parts.map((p, i) => parseColorStop(p, i, parts.length)).filter(Boolean);
    const a = (angle * Math.PI) / 180;
    const dx = Math.sin(a);
    const dy = -Math.cos(a);
    const half = (Math.abs(w * dx) + Math.abs(h * dy)) / 2;
    const g = ctx.createLinearGradient(w / 2 - dx * half, h / 2 - dy * half, w / 2 + dx * half, h / 2 + dy * half);
    addStops(g, stops);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  } else if (rad) {
    const parts = splitTopLevel(rad[1]);
    const shape = parts[0].match(/ellipse\s+([\d.]+)%\s+([\d.]+)%\s+at\s+([\d.]+)%\s+([\d.]+)%/i);
    if (shape) parts.shift();
    const rx = Math.max(1, ((shape ? num(shape[1]) : 50) / 100) * w);
    const ry = Math.max(1, ((shape ? num(shape[2]) : 50) / 100) * h);
    const cx = ((shape ? num(shape[3]) : 50) / 100) * w;
    const cy = ((shape ? num(shape[4]) : 50) / 100) * h;
    const stops = parts.map((p, i) => parseColorStop(p, i, parts.length)).filter(Boolean);
    ctx.translate(cx, cy);
    ctx.scale(1, ry / rx);
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
    addStops(g, stops);
    ctx.fillStyle = g;
    ctx.fillRect(-cx, -cy * (rx / ry), w, h * (rx / ry));
  } else {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);
  }
  ctx.restore();
}

/** Server `video_filters._apply_fade` alpha for `fade_in` / `fade_out` at clip progress 0…1. */
export function fadeFilterAlpha(filterId, intensity, progress) {
  const amount = Math.max(0, Math.min(1, num(intensity, 50) / 100));
  const edge = Math.max(0.05, amount * 0.5);
  const p = Math.max(0, Math.min(1, num(progress)));
  if (filterId === 'fade_in') return p < edge ? Math.min(1, p / edge) : 1;
  if (filterId === 'fade_out') return p < 1 - edge ? 1 : Math.max(0, (1 - p) / edge);
  return 1;
}

export function roundedRectPath(ctx, x, y, w, h, r) {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}
